/**
 * supabaseEdge.ts
 * 
 * Helper centralizado para chamadas a Edge Functions do Supabase.
 * 
 * Benefícios:
 * - URL e ANON_KEY centralizados
 * - Headers CORS configurados
 * - Timeout configurável
 * - Tratamento de erro padronizado
 * - Logs mínimos sem PII
 */

// ============================================================================
// CONFIGURAÇÃO CENTRALIZADA
// ============================================================================

/**
 * Este projeto roda em múltiplos ambientes (preview/published).
 * Em preview, o Supabase pode ter um Project Ref diferente do published.
 * Então priorizamos VITE_* quando estiver disponível e só caímos em hardcode
 * como fallback seguro (para evitar "apikey" vazio).
 */
const ENV_PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID as string | undefined;
const ENV_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const ENV_ANON_KEY = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY) as string | undefined;

// Fallback público (não é segredo)
const FALLBACK_PROJECT_ID = 'fhhsntxcvegljurhdtjd';
const FALLBACK_URL = `https://${FALLBACK_PROJECT_ID}.supabase.co`;
const FALLBACK_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZoaHNudHhjdmVnbGp1cmhkdGpkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYzNDE2NjMsImV4cCI6MjA4MTkxNzY2M30.qXzMd6XmGtl--7CbvOoL73-ADJ_DxO-eE7ZXBiSJibM';

export const SUPABASE_PROJECT_ID = ENV_PROJECT_ID || FALLBACK_PROJECT_ID;
export const SUPABASE_URL = ENV_URL || `https://${SUPABASE_PROJECT_ID}.supabase.co` || FALLBACK_URL;
export const SUPABASE_ANON_KEY = ENV_ANON_KEY || FALLBACK_ANON_KEY;

// ============================================================================
// TIPOS
// ============================================================================

export interface EdgeFunctionOptions {
  /** Timeout em ms (padrão: 30s) */
  timeout?: number;
  /** Headers customizados */
  headers?: Record<string, string>;
  /** Método HTTP (padrão: POST) */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
}

export interface EdgeFunctionResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  status: number;
  functionVersion?: string;
}

// ============================================================================
// HELPER PRINCIPAL
// ============================================================================

/**
 * Chama uma Edge Function do Supabase de forma segura.
 * 
 * @param functionName Nome da função (ex: 'create-public-order')
 * @param body Payload JSON (opcional)
 * @param options Opções (timeout, headers, método)
 * @returns Promise com resultado padronizado
 * 
 * @example
 * const result = await callEdgeFunction('submit-public-review', { token, notaGeral: 5 });
 * if (result.success) {
 *   console.log(result.data);
 * } else {
 *   toast({ title: 'Erro', description: result.error });
 * }
 */
export async function callEdgeFunction<T = unknown>(
  functionName: string,
  body?: Record<string, unknown> | null,
  options: EdgeFunctionOptions = {}
): Promise<EdgeFunctionResult<T>> {
  const { 
    timeout = 30000, 
    headers = {}, 
    method = 'POST' 
  } = options;

  const url = `${SUPABASE_URL}/functions/v1/${functionName}`;
  
  // Controller para timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    // Log mínimo (sem dados sensíveis)
    if (import.meta.env.DEV) {
      console.log(`[Edge] → ${functionName}`);
    }

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Capturar X-Function-Version
    const functionVersion = response.headers.get('X-Function-Version') || undefined;

    // Parsear resposta
    let data: T | undefined;
    let errorMessage: string | undefined;

    try {
      const json = await response.json();
      if (response.ok) {
        data = json as T;
      } else {
        errorMessage = json?.error || json?.message || `Erro ${response.status}`;
      }
    } catch {
      if (!response.ok) {
        errorMessage = `Erro ${response.status}: ${response.statusText}`;
      }
    }

    // Log de resultado com versão
    console.log(`[Edge] ← ${functionName}: ${response.ok ? 'OK' : errorMessage}${functionVersion ? ` | version=${functionVersion}` : ''}`);

    return {
      success: response.ok,
      data,
      error: errorMessage,
      status: response.status,
      functionVersion,
    };
  } catch (err) {
    clearTimeout(timeoutId);

    // Erro de timeout
    if (err instanceof Error && err.name === 'AbortError') {
      console.warn(`[Edge] ⏱ ${functionName}: Timeout após ${timeout}ms`);
      return {
        success: false,
        error: 'Tempo limite excedido. Tente novamente.',
        status: 0,
      };
    }

    // Erro de rede
    console.error(`[Edge] ✖ ${functionName}:`, err);
    return {
      success: false,
      error: 'Erro de conexão. Verifique sua internet.',
      status: 0,
    };
  }
}

// ============================================================================
// HELPERS ESPECÍFICOS (tipados)
// ============================================================================

/**
 * Submete uma avaliação pública via token.
 * REQUER autenticação do cliente - o token JWT é enviado via header.
 */
export async function submitPublicReview(payload: {
  token: string;
  notaGeral: number;
  notaSabor?: number;
  notaHigiene?: number;
  notaAtendimento?: number;
  comentario?: string;
}) {
  // Usar helper autenticado para enviar o token do cliente
  return callClienteEdgeFunction<{ success: boolean; message: string }>('submit-public-review', payload);
}

/**
 * Cria um pedido público (cardápio digital).
 * Envia itens no formato original - a Edge Function valida preços no backend.
 */
export async function createPublicOrder(payload: {
  unidadeId: number;
  itens: Array<{ 
    id: number;
    nome: string;
    preco: number;
    quantidade: number; 
    observacao?: string;
    observacoes?: string[];  // Array de observações por unidade
  }>;
  clienteNome: string;
  clienteWhatsapp: string;
  clienteAuthId?: number;
  observacoes?: string;
  /** Se true, aplica cashback disponível no pedido */
  usarCashback?: boolean;
}) {
  return callEdgeFunction<{ success: boolean; pedidoId: number; cashbackAplicado?: number }>('create-public-order', {
    unidadeId: payload.unidadeId,
    itens: payload.itens.map(item => ({
      ...item,
      observacoes: item.observacoes?.filter(Boolean),
    })),
    clienteNome: payload.clienteNome,
    clienteWhatsapp: payload.clienteWhatsapp,
    clienteAuthId: payload.clienteAuthId,
    observacoes: payload.observacoes,
    usarCashback: payload.usarCashback,
  });
}

/**
 * Envia OTP para WhatsApp.
 */
export async function enviarOtpWhatsapp(whatsapp: string, tipo = 'cadastro') {
  return callEdgeFunction<{ success: boolean; simulacao?: boolean; expiresIn?: number }>('enviar-otp-whatsapp', { whatsapp, tipo });
}

/**
 * Cadastra cliente via token de avaliação.
 */
export async function cadastrarViaAvaliacao(dados: {
  tokenAvaliacao: string;
  nome: string;
  senha: string;
}) {
  return callEdgeFunction<{ success: boolean; token: string; cliente: { id: number; nome: string; whatsapp: string } }>('cliente-cadastrar-via-avaliacao', dados);
}

/**
 * Verifica se existe conta de cliente para um WhatsApp.
 * Não expõe dados sensíveis, apenas retorna exists: boolean.
 */
export async function checkClienteAccount(whatsapp: string) {
  return callEdgeFunction<{ exists: boolean }>('cliente-check-account', { whatsapp });
}

// ============================================================================
// HELPER PARA CHAMADAS AUTENTICADAS DE OPERADOR
// ============================================================================

/**
 * Chama uma Edge Function com o token do operador autenticado (Supabase Auth).
 * Extrai o token da sessão atual e envia via header Authorization.
 */
export async function callAuthenticatedEdgeFunction<T = unknown>(
  functionName: string,
  body?: Record<string, unknown> | null,
  options: EdgeFunctionOptions = {}
): Promise<EdgeFunctionResult<T>> {
  // Importar supabase dinamicamente para evitar dependência circular
  const { supabase } = await import('@/integrations/supabase/client');
  
  // Obter sessão atual
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    return {
      success: false,
      error: 'Usuário não autenticado',
      status: 401,
    };
  }

  // Adicionar token ao header
  const headers = {
    ...options.headers,
    Authorization: `Bearer ${session.access_token}`,
  };

  return callEdgeFunction<T>(functionName, body, { ...options, headers });
}

// ============================================================================
// HELPER PARA CRIAÇÃO DE ENTREGA (OPERADOR)
// ============================================================================

export interface CriarEntregaPayload {
  pedido_id: number;
  pickup_lat: number;
  pickup_lng: number;
  pickup_endereco?: string;
  dropoff_lat: number;
  dropoff_lng: number;
  dropoff_endereco: string;
  dist_km_estimada: number;
  taxa_entrega: number;
}

export interface CriarEntregaResult {
  success: boolean;
  entrega_id: number;
  entregadores_notificados: number;
}

/**
 * Cria uma entrega e notifica entregadores via Edge Function.
 * A Edge Function lida com broadcast para todos os entregadores elegíveis.
 */
export async function criarEntrega(payload: CriarEntregaPayload) {
  return callAuthenticatedEdgeFunction<CriarEntregaResult>('entrega-criar', payload as unknown as Record<string, unknown>);
}

// ============================================================================
// HELPER PARA CHAMADAS AUTENTICADAS DE CLIENTE
// ============================================================================

/**
 * Chama uma Edge Function com o token do cliente autenticado.
 * O token é extraído do localStorage e enviado via header X-Cliente-Token.
 */
export async function callClienteEdgeFunction<T = unknown>(
  functionName: string,
  body?: Record<string, unknown> | null,
  options: EdgeFunctionOptions = {}
): Promise<EdgeFunctionResult<T>> {
  // Extrair token da sessão do cliente
  let clienteToken: string | null = null;
  try {
    const session = localStorage.getItem('sf_cliente_session');
    if (session) {
      const parsed = JSON.parse(session);
      clienteToken = parsed.token || null;
    }
  } catch {
    console.warn('[callClienteEdgeFunction] Erro ao extrair token da sessão');
  }

  // Adicionar token ao header
  const headers = {
    ...options.headers,
    ...(clienteToken ? { 'X-Cliente-Token': clienteToken } : {}),
  };

  return callEdgeFunction<T>(functionName, body, { ...options, headers });
}
